import { useMemo } from 'react';
import {
  Box,
  IconButton,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverArrow,
  PopoverCloseButton,
  PopoverBody,
  UnorderedList,
  ListItem,
} from '@chakra-ui/react';
import PropTypes from 'prop-types';
import { FiMoreHorizontal } from 'react-icons/fi';

const cleanString = (string) => {
  const noSpecials = string.replace(/[^a-zA-Z ]/g, '');
  return noSpecials.toLowerCase().replace(/ /g, '_');
};
/**
 * Truncates strings at word boundary
 * WARN: prepare data with src/app/services/helpers.js#addTruncatedProperties
 * @param {string} title - The title appears in the Popover
 * @param {array} row - The list of items to display
 * @param {string} fieldName - The list of items to display
 * @returns {Element}
 */
const TruncatedArrayComponent = ({ title, fieldName, row, renderItems, renderTruncated }) => {
  // create a lowercase all alphabetic string for HTML attributes
  const list = row[`${fieldName}Popover`] ?? [];
  const truncatedList = row[`${fieldName}TruncatedList`] ?? [];
  const truncatedString = row[`${fieldName}Truncated`] ?? '';
  const cleanTitle = useMemo(() => {
    return cleanString(title);
  }, [title]);

  if (truncatedString.split(',')?.length < list?.length) {
    const trigger = (
      <Popover placement="bottom-start">
        <PopoverTrigger>
          <IconButton variant="ghost" colorScheme={'cyan'} aria-label={`view ${cleanTitle}`} icon={<FiMoreHorizontal />} />
        </PopoverTrigger>
        <PopoverContent minW={{ base: '100%', lg: 'max-content' }} aria-label="popover content">
          <PopoverHeader fontWeight="semibold">{title}</PopoverHeader>
          <PopoverArrow />
          <PopoverCloseButton />
          <PopoverBody
            maxH="400px"
            maxW="500px"
            overflowY="scroll"
            sx={{
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                width: '8px',
                backgroundColor: list.length >= 20 ? 'gray.100' : 'white',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'gray.600',
                borderRadius: '24px',
              },
            }}
          >
            {renderItems ? renderItems(list) : (
              <UnorderedList>
                {list.map((facility, i) => {
                  return <ListItem key={`${facility.id}-${i}`}>{facility.value}</ListItem>;
                })}
              </UnorderedList>
            )}
          </PopoverBody>
        </PopoverContent>
      </Popover>
    );

    // When a custom chip renderer is provided, keep the chips and the "..." trigger in a
    // single flex-wrap row so the trigger flows onto the last chip line instead of dropping below.
    if (renderTruncated) {
      return (
        <Box display="flex" flexWrap="wrap" alignItems="center" gap={1}>
          {renderTruncated(truncatedList)}
          {trigger}
        </Box>
      );
    }

    return (
      <span>
        <span aria-label={`truncated ${cleanTitle}`}>{truncatedString + ` `}</span>
        {trigger}
      </span>
    );
  } else {
    return renderTruncated
      ? (
        <Box display="flex" flexWrap="wrap" alignItems="center" gap={1}>
          {renderTruncated(list)}
        </Box>
      )
      : <span aria-label={cleanTitle}>{truncatedString}</span>;
  }
};

TruncatedArrayComponent.propTypes = {
  title: PropTypes.string,
  fieldName: PropTypes.string,
  row: PropTypes.object,
  renderItems: PropTypes.func,
  renderTruncated: PropTypes.func,
};

export default TruncatedArrayComponent;
